import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon, 
  LinkIcon, 
  ShareIcon, 
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { referralService } from '../../services/referralService';

const Referrals = () => {
  const [stats, setStats] = useState({
    totalReferrals: 0,
    pendingReferrals: 0,
    approvedReferrals: 0,
    phoneNumbersTracked: 0,
    registeredReferrals: 0,
  });
  const [referrals, setReferrals] = useState([]);
  const [referralLink, setReferralLink] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [copied, setCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsResponse, referralsResponse, linkResponse] = await Promise.all([
        referralService.getStats(),
        referralService.getList({ status: filter === 'all' ? undefined : filter }),
        referralService.getLink(),
      ]);

      setStats(statsResponse.data.data);
      setReferrals(referralsResponse.data.data.referrals);
      setReferralLink(linkResponse.data.data.referralLink);
      // Set referral code, ensuring it's a string
      const code = linkResponse.data.data.referralCode;
      setReferralCode(code ? String(code) : '');
    } catch (error) {
      console.error('Error loading referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const shareReferralLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Jhonks - Get a Free Vacuum Cleaner!',
          text: 'Sell your waste materials and earn money. Get a free vacuum cleaner when you join through our referral program!',
          url: referralLink,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback to copying to clipboard
      copyToClipboard();
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Approved
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <ClockIcon className="w-3 h-3 mr-1" />
        Pending
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const FilterButton = ({ value, label }) => (
    <button
      onClick={() => setFilter(value)}
      className={`px-4 py-2 text-sm font-medium rounded-md ${
        filter === value
          ? 'bg-green-600 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Referral Program</h1>
            <p className="text-gray-600">Earn rewards by referring friends to Jhonks</p>
          </div>
          <UserGroupIcon className="h-8 w-8 text-green-600" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserGroupIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Referrals</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalReferrals}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pendingReferrals}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Approved</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.approvedReferrals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <PhoneIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Phone Numbers Tracked</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.phoneNumbersTracked}</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserGroupIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Registered Users</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.registeredReferrals}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">How Referrals Work</h3>
        <div className="space-y-2 text-blue-800">
          <p>• Share your referral link with friends and family</p>
          <p>• When someone clicks your link and enters their phone number, it's tracked</p>
          <p>• If they register on Jhonks, they become a registered referral</p>
          <p>• <strong>Your referral is approved after they sell at least 5kg of products</strong></p>
          <p>• Approved referrals count towards your rewards and bonuses</p>
        </div>
      </div>

      {/* Referral Link Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Your Referral Link</h2>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              value={referralLink}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <button
            onClick={copyToClipboard}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={shareReferralLink}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
          >
            <ShareIcon className="h-4 w-4 mr-2" />
            Share
          </button>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Share this link with your friends. They'll get a free vacuum cleaner when they join!
        </p>
      </div>

      {/* Referral Code Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Your Referral Code</h2>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              value={loading ? 'Loading...' : (referralCode || 'No code available')}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono text-center text-lg font-semibold"
            />
          </div>
          <button
            onClick={async () => {
              if (referralCode && !loading) {
                try {
                  await navigator.clipboard.writeText(referralCode);
                  setCodeCopied(true);
                  setTimeout(() => setCodeCopied(false), 2000);
                } catch (error) {
                  console.error('Failed to copy:', error);
                }
              }
            }}
            disabled={!referralCode || loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
            {codeCopied ? 'Copied!' : 'Copy Code'}
          </button>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Share your referral code with friends. They can enter it during registration!
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Filter by status:</span>
          <FilterButton value="all" label="All" />
          <FilterButton value="pending" label="Pending" />
          <FilterButton value="approved" label="Approved" />
        </div>
      </div>

      {/* Referrals List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Your Referrals</h2>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          </div>
        ) : referrals.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No referrals yet</p>
            <p className="text-sm">Share your referral link to start earning rewards!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {referrals.map((referral) => (
                  <tr key={referral._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {referral.referredUser 
                          ? `${referral.referredUser.firstName} ${referral.referredUser.lastName}`
                          : 'Not registered yet'
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {referral.referredUser?.displayPhoneNumber || 
                       referral.displayPhoneNumber || 
                       (referral.referredUser?.phoneNumber 
                         ? (referral.referredUser.phoneNumber.startsWith('234') 
                            ? `0${referral.referredUser.phoneNumber.slice(3)}` 
                            : referral.referredUser.phoneNumber)
                         : (referral.referredPhoneNumber 
                            ? (referral.referredPhoneNumber.startsWith('234') 
                               ? `0${referral.referredPhoneNumber.slice(3)}` 
                               : referral.referredPhoneNumber)
                            : 'N/A'))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(referral.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(referral.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {referral.totalSales}kg
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ChartBarIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>How it works:</strong> Referrals are approved after the person you referred sells at least 5kg of products. 
              You'll earn rewards for each approved referral!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
